/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {Report} from '../../../../../model';
import {ReportService} from '../../../../../services/report.service';
import {CannedResponseProvider, ReportDelayProvider} from '../../../../../providers';
import {ReportDelayResult, DelayType} from '../../../../../model/report';
import { CannedResponse } from 'src/app/modules/manage/model/canned-response/canned-response';
import { CannedResponseType } from 'src/app/modules/manage/model/canned-response/response.type';
import { LogService } from 'src/app/services';

@Component({
  selector: 'report-delay-modal',
  templateUrl: './report-delay.modal.html',
  styleUrls: ['./report-delay.modal.scss']
})
export class ReportDelayModal implements OnInit, OnDestroy {
  subject: Subject<boolean>;
  subscription: any;

  currentReport: Report;
  private reportSub: any;
  DelayType = DelayType;

  isDelayed = false;
  reason: string;
  notes: string;
  delay: DelayType = DelayType.CLIENT;
  notify = false;
  submitting: boolean = false;

  cannedResponses: CannedResponse[] = []

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    private reportService: ReportService,
    private reportDelayProvider: ReportDelayProvider,
    private cannedResponseProvider: CannedResponseProvider,
    private logService : LogService
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });

    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;

      if (this.currentReport)
        if (this.currentReport.ID)
          if (this.currentReport.ACTIVE_DELAY) {
            this.isDelayed = true;
            this.delay = this.currentReport.ACTIVE_DELAY.DELAY_TYPE;
          }

    });

    this.loadCannedResponse();
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
    if (this.reportSub) this.reportSub.unsubscribe();
  }

  onConfirm(): void {
    if (this.isDelayed)
      this.stopDelay();
    else this.startDelay();
  }

  private startDelay(): void {
    this.submitting = true;
    this.reportDelayProvider.startDelay(this.currentReport.ID, this.notify, this.delay, this.reason, this.notes)
      .subscribe(
        data => {
          if (data.success) {
            const res = ReportDelayResult.parse(data.result);
            this.currentReport.getDelays().push(res.DELAY);
            this.currentReport.ACTIVE_DELAY = res.DELAY;
            this.currentReport.STATE = res.STATE;
            this.hideModal();
          } else {
            this.logService.error(JSON.stringify(data.result, null, 2));
          }
          this.submitting = false;
        });
  }

  private stopDelay(): void {
    this.submitting = true;
    this.reportDelayProvider.stopDelay(this.currentReport.ID, this.notify, this.delay, this.reason, this.notes)
      .subscribe(
        data => {
          if (data.success) {
            const res = ReportDelayResult.parse(data.result);
            for (let i = 0; i < this.currentReport.getDelays().length; i++) {
              if (this.currentReport.getDelays()[i].ID === res.DELAY.ID) {
                this.currentReport.getDelays()[i] = res.DELAY;
              }
            }

            this.currentReport.STATE = res.STATE;
            this.currentReport.ACTIVE_DELAY = null;
            this.hideModal();
          } else {
            this.logService.error(JSON.stringify(data.result, null, 2));
          }
          this.submitting = false;
        });
  }

  private hideModal(): void {
    this.bsModalRef.hide();
    this.subject.next(true);
    this.subject.complete();
  }


  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next(null);
    this.subject.complete();
  }

  isInputValid(): boolean {
    return !!this.notes;
  }

  appendCannedAction(action: string): void {
    let newLine = false;
    if(this.notes){
      if(this.notes.length > 0)
        newLine = true;
    }else this.notes = '';

    if(action)
      this.notes += (newLine ? ' ' : '') + `${action}`;
  }

  appendCannedActionReason(action: string): void {
    let newLine = false;
    if(this.reason){
      if(this.reason.length > 0)
        newLine = true;
    }else this.reason = '';

    if (action)
      this.reason += (newLine ? ' ' : '') + `${action}`;
  }

  private loadCannedResponse(): void {
    this.cannedResponseProvider.getCannedResponses(1, null, CannedResponseType.DELAY)
      .subscribe(data => {
        if(data.success){
          this.cannedResponses = CannedResponse.parseArray(data.result.items);
        }
      })
  }
}
